import { ApiException } from "@/types/api"
import { oktaAuth } from "@/auth/oktaConfig"

/**
 * API calls use relative paths (e.g. /api/v1/config/gap/devices).
 * - In dev: Vite proxies /api/* to localhost (see vite.config.ts).
 * - In QA/prod: Same origin; gateway routes /api/* to the real API.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}

  try {
    const accessToken = await oktaAuth.getAccessToken()
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`
    }
  } catch (error) {
    console.warn("Failed to get auth token:", error)
  }

  return headers
}

export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const hasFormDataBody =
    typeof FormData !== "undefined" && options?.body instanceof FormData

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      ...(hasFormDataBody ? {} : { "Content-Type": "application/json" }),
      ...authHeaders,
      ...options?.headers,
    },
  })

  if (!response.ok) {
    let errorMessage = "API request failed"
    let errorPayload: unknown
    try {
      errorPayload = await response.json()
      if (
        typeof errorPayload === "object" &&
        errorPayload !== null &&
        "message" in errorPayload &&
        typeof errorPayload.message === "string"
      ) {
        errorMessage = errorPayload.message
      }
    } catch {
      // Response body is not JSON
    }

    throw new ApiException({
      code: response.status,
      message: errorMessage,
      payload: errorPayload,
    })
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null as T
  }

  return response.json()
}
